import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';
import { COLOR_NAMES } from '../../api/drape';
import ColorCombinationCard from './ColorCombinationCard';
import BlurredLockOverlay from './BlurredLockOverlay';

function getColorName(hex: string): string | undefined {
  return COLOR_NAMES[hex] || undefined;
}

export interface PaletteResultPalette {
  name?: string;
  colors: string[];
}

export interface PaletteResultProps {
  palettes: PaletteResultPalette[];
  onCopyPalette: () => Promise<void>;
  /** Copy a single palette by index (for "Copy all" on each card) */
  onCopyPaletteAtIndex?: (index: number) => Promise<void>;
  onCopyCss?: () => Promise<void>;
  onCopyColor: (color: string) => void;
  canExport?: boolean;
  selectedPaletteIndex: number | null;
  onSelectPaletteIndex: (index: number | null) => void;
  onRemoveColor?: (paletteIndex: number, colorIndex: number) => void;
  isPublicPreview?: boolean;
  onExportLockPress?: () => void;
}

const PaletteResult: React.FC<PaletteResultProps> = ({
  palettes,
  onCopyPalette: _onCopyPalette,
  onCopyPaletteAtIndex,
  onCopyCss: _onCopyCss,
  onCopyColor,
  canExport: _canExport = false,
  selectedPaletteIndex: _selectedPaletteIndex,
  onSelectPaletteIndex: _onSelectPaletteIndex,
  onRemoveColor: _onRemoveColor,
  isPublicPreview = false,
  onExportLockPress: _onExportLockPress,
}) => {
  const { colors } = useTheme();

  const hasContent =
    palettes.length > 0 && palettes.some((p) => p.colors.length > 0);

  if (!hasContent) {
    return (
      <View
        style={[
          styles.resultsCard,
          {
            backgroundColor: colors.border + '18',
            borderColor: colors.border,
          },
        ]}
        accessibilityLabel="Palette results"
        accessibilityRole="region"
      >
        <Text style={[styles.resultsLabel, { color: colors.secondary }]}>
          Results
        </Text>
        <View style={styles.emptyState}>
          <View style={styles.emptyIconWrap}>
            <Ionicons
              name="color-palette-outline"
              size={40}
              color={colors.secondary}
            />
          </View>
          <Text style={[styles.emptyText, { color: colors.secondary }]}>
            Generate or Randomize above to create your palette
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.resultsCard,
        {
          backgroundColor: colors.border + '18',
          borderColor: colors.border,
        },
      ]}
      accessibilityLabel="Palette results"
      accessibilityRole="region"
    >
      <Text style={[styles.resultsLabel, { color: colors.secondary }]}>
        Results
      </Text>
      <View style={styles.paletteList}>
        {palettes.map((palette, comboIndex) => {
          const card = (
            <ColorCombinationCard
              key={comboIndex}
              name={palette.name || `Palette ${comboIndex + 1}`}
              colors={palette.colors}
              colorNames={palette.colors.map((hex) => getColorName(hex))}
              onCopyColor={onCopyColor}
              onCopyAll={
                onCopyPaletteAtIndex
                  ? () => onCopyPaletteAtIndex(comboIndex)
                  : undefined
              }
            />
          );
          return (
            <View key={`combo-${comboIndex}`}>
              {isPublicPreview && comboIndex >= 1 ? (
                <BlurredLockOverlay blurAmount={5} overlayOpacity={0.55}>
                  {card}
                </BlurredLockOverlay>
              ) : (
                card
              )}
            </View>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  resultsCard: {
    width: '100%',
    borderRadius: 16,
    padding: 20,
    marginTop: 0,
    marginBottom: 0,
    borderWidth: 1,
  },
  resultsLabel: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1.2,
    marginBottom: 14,
    textTransform: 'uppercase',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 28,
    paddingHorizontal: 20,
  },
  emptyIconWrap: {
    marginBottom: 10,
    opacity: 0.6,
  },
  emptyText: {
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
  },
  paletteList: {
    gap: 12,
  },
});

export default PaletteResult;
